import {CommonModule} from '@angular/common';
import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {FlexLayoutModule} from '@angular/flex-layout';
import {MatBadgeModule} from "@angular/material/badge";
import {MatButtonModule} from "@angular/material/button";
import {MatButtonToggleModule} from '@angular/material/button-toggle';
import {MatCheckboxModule} from '@angular/material/checkbox';
import {MatRadioModule} from '@angular/material/radio';
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderRow,
  MatHeaderRowDef,
  MatRow,
  MatRowDef,
  MatTable,
  MatTableDataSource,
  MatTableModule
} from "@angular/material/table";
import {DateUtilsService} from "@shared-services/dateUtils.service";
import {UtilsService} from "@shared-services/utils.service";
import {
  OccupancyType,
  PaymentConfigRequest,
  SupplierSourceConfig,
  TransactionSource,
  TransactionSourceConfig
} from "../../../leasing/leasing.model";
import {ImportAgreementType} from "../../run-tenant-payouts/model/run-payouts.model";
import {RunPayoutsConfigService} from "../../run-tenant-payouts/service/run-payouts-config.service";
import {ImportProductsConfig, ProductLinkStatus} from "../model/run-products.model";
import {RunProductsConfigService} from "../service/run-products-config.service";

@Component({
  selector: 'app-products-import-config',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonToggleModule,
    MatCheckboxModule,
    MatRadioModule,
    FlexLayoutModule,
    MatButtonModule,
    MatCell,
    MatCellDef,
    MatColumnDef,
    MatHeaderCell,
    MatHeaderRow,
    MatHeaderRowDef,
    MatRow,
    MatRowDef,
    MatTable,
    MatTableModule,
    MatBadgeModule
  ],
  templateUrl: './products-import-config.component.html'
})
export class ProductsImportConfigComponent implements OnInit, OnChanges {
  @Input() importConfig!: ImportProductsConfig;
  @Output() importConfigChange = new EventEmitter<ImportProductsConfig>();
  @Output() saveImportConfig = new EventEmitter<void>();

  // Track modes explicitly
  private transactionSourcesMode: 'all' | 'custom' = 'all';
  private paymentConfigsMode: 'all' | 'custom' = 'all';

  // Enum references for template
  readonly ImportAgreementType = ImportAgreementType;

  displayColumns = ['retailerId', 'retailerCompanyName', 'startDate', 'endDate', 'include', 'exclude'];
  offeringDisplayColumns = ['uuid', 'displayName', 'startDate', 'endDate', 'include', 'exclude'];

  // Mock master configurations
  masterConfigs: {
    single: PaymentConfigRequest[],
    shared: PaymentConfigRequest[],
    offering: PaymentConfigRequest[]
  } = {single: [], shared: [], offering: []};

  masterSingleDataSource = new MatTableDataSource<PaymentConfigRequest>([]);
  masterSharedDataSource = new MatTableDataSource<PaymentConfigRequest>([]);
  masterOfferingDataSource = new MatTableDataSource<PaymentConfigRequest>([]);

  // Mock shared configurations with parent grouping
  sharedConfigs: {
    shared: {
      parentName: string,
      configs: PaymentConfigRequest[]
    }[],
    offering: {
      parentName: string,
      configs: PaymentConfigRequest[]
    }[]
  } = {shared: [], offering: []}

  sharedConfigsDataSource = new MatTableDataSource<PaymentConfigRequest>([]);
  offeringConfigsDataSource = new MatTableDataSource<PaymentConfigRequest>([]);

  constructor(private productsConfigService: RunProductsConfigService,
              private payoutsConfigService: RunPayoutsConfigService,
              public dateUtils: DateUtilsService,
              public utils: UtilsService) {
  }

  ngOnInit() {
    // Initialize with defaults if not set
    if (!this.importConfig.transactionSources) {
      this.importConfig.transactionSources = [];
    }
    if (!this.importConfig.includePaymentConfigUuids) {
      this.importConfig.includePaymentConfigUuids = [];
    }
    if (!this.importConfig.excludePaymentConfigUuids) {
      this.importConfig.excludePaymentConfigUuids = [];
    }

    // Initialize modes based on current state
    this.initImportConfig();
  }

  ngOnChanges(changes: SimpleChanges) {
    this.initImportConfig();
    this.loadPaymentConfigs();
  }

  initImportConfig() {
    this.transactionSourcesMode = this.importConfig.transactionSources.length === 0 ? 'all' : 'custom';
    this.paymentConfigsMode = this.isPaymentConfigsAllModeByData() ? 'all' : 'custom';
  }

  loadPaymentConfigs() {
    this.payoutsConfigService.getAllPaymentConfigs().subscribe({
      next: value => {
        this.masterConfigs = {
          single: value.leasePaymentConfigs.filter(f => f.occupancyType == OccupancyType.SINGLE && !f.transactionSource?.includes('SUPPLIER')),
          shared: value.leasePaymentConfigs.filter(f => f.occupancyType == OccupancyType.SHARED && !f.transactionSource?.includes('SUPPLIER')),
          offering: value.offeringPaymentConfigs
        }

        this.sharedConfigs = {
          shared: this.createSharedConfigsMap(this.masterConfigs.shared, value.leasePaymentConfigs, 'SUPPLIER_LEASE'),
          offering: this.createSharedConfigsMap(this.masterConfigs.offering, value.leasePaymentConfigs, 'SUPPLIER_OFFERING')
        };

        this.updateDataSources(); // Add this line
      },
      error: error => {
        console.error('Error loading payment configs:', error);
      }
    })
  }

  private createSharedConfigsMap(masterConfigs: PaymentConfigRequest[], allConfigs: PaymentConfigRequest[], sourceSuffix: string): {
    parentName: string,
    configs: PaymentConfigRequest[]
  }[] {
    return masterConfigs.map(masterConfig => {
      const childConfigs = allConfigs.filter(config => {
        const ts = config.transactionSource!.includes(sourceSuffix)
        if (!ts) return false;
        if (config.transactionSourceConfig) {
          try {
            const parentUuid = this.parseSupplierSourceConfig(config.transactionSourceConfig).paymentConfigUuid;
            return parentUuid === masterConfig.uuid;
          } catch (error) {
            console.warn('Error parsing transactionSourceConfig for config:', config.uuid, error);
            return false;
          }
        }
        return false;
      });
      return {
        parentName: masterConfig.retailerCompanyName || this.parseMainSourceConfig(masterConfig.transactionSourceConfig!).domainPrefix || 'Unknown', // You might want to use a different property for the name
        configs: childConfigs
      };
    });
  }

  private emitConfigChange() {
    this.importConfigChange.emit(this.importConfig);
  }

  // Transaction Sources methods
  isTransactionSourcesAllMode(): boolean {
    return this.transactionSourcesMode === 'all';
  }

  setTransactionSourcesMode(isAll: boolean) {
    this.transactionSourcesMode = isAll ? 'all' : 'custom';
    if (isAll) {
      this.importConfig.transactionSources = [];
    }
    this.emitConfigChange();
  }

  isTransactionSourceSelected(source: TransactionSource): boolean {
    return this.importConfig.transactionSources.includes(source);
  }

  // Update existing methods to refresh datasources
  toggleTransactionSource(source: TransactionSource) {
    const index = this.importConfig.transactionSources.indexOf(source);
    if (index > -1) {
      this.importConfig.transactionSources.splice(index, 1);
    } else {
      this.importConfig.transactionSources.push(source);
    }
    this.updateDataSources(); // Add this line
    this.emitConfigChange();
  }

  // Payment Config methods
  isPaymentConfigsAllMode(): boolean {
    return this.paymentConfigsMode === 'all';
  }

  private isPaymentConfigsAllModeByData(): boolean {
    return (this.importConfig.includePaymentConfigUuids?.length || 0) === 0 &&
      (this.importConfig.excludePaymentConfigUuids?.length || 0) === 0;
  }

  setPaymentConfigsMode(isAll: boolean) {
    this.paymentConfigsMode = isAll ? 'all' : 'custom';
    if (isAll) {
      this.importConfig.includePaymentConfigUuids = [];
      this.importConfig.excludePaymentConfigUuids = [];
    }
    this.emitConfigChange();
  }

  isPaymentConfigIncluded(configUuid: string): boolean {
    return this.importConfig.includePaymentConfigUuids?.includes(configUuid) || false;
  }

  isPaymentConfigExcluded(configUuid: string): boolean {
    return this.importConfig.excludePaymentConfigUuids?.includes(configUuid) || false;
  }

  togglePaymentConfigInclude(configUuid: string) {
    if (this.isPaymentConfigIncluded(configUuid)) {
      // Remove from include list
      this.importConfig.includePaymentConfigUuids = this.importConfig.includePaymentConfigUuids?.filter(uuid => uuid !== configUuid) || [];
    } else {
      // Add to include list and remove from exclude list
      this.importConfig.includePaymentConfigUuids = this.importConfig.includePaymentConfigUuids || [];
      this.importConfig.includePaymentConfigUuids.push(configUuid);
      this.importConfig.excludePaymentConfigUuids = this.importConfig.excludePaymentConfigUuids?.filter(uuid => uuid !== configUuid) || [];
    }
    this.emitConfigChange();
  }

  togglePaymentConfigExclude(configUuid: string) {
    if (this.isPaymentConfigExcluded(configUuid)) {
      // Remove from exclude list
      this.importConfig.excludePaymentConfigUuids = this.importConfig.excludePaymentConfigUuids?.filter(uuid => uuid !== configUuid) || [];
    } else {
      // Add to exclude list and remove from include list
      this.importConfig.excludePaymentConfigUuids = this.importConfig.excludePaymentConfigUuids || [];
      this.importConfig.excludePaymentConfigUuids.push(configUuid);
      this.importConfig.includePaymentConfigUuids = this.importConfig.includePaymentConfigUuids?.filter(uuid => uuid !== configUuid) || [];
    }
    this.emitConfigChange();
  }

  unselectAll() {
    this.importConfig.includePaymentConfigUuids = [];
    this.importConfig.excludePaymentConfigUuids = [];
    this.emitConfigChange();
  }

  getTransactionSourceValues(): TransactionSource[] {
    return Object.values(TransactionSource);
  }

  getImportAgreementTypeValues(): ImportAgreementType[] {
    return Object.values(ImportAgreementType);
  }

  getProductLinkStatusValues(): ProductLinkStatus[] {
    return Object.values(ProductLinkStatus);
  }

  selectImportAgreementType(type: ImportAgreementType | null) {
    this.importConfig.importAgreementType = type;
    this.emitConfigChange();
  }

  selectProductLinkStatus(type: ProductLinkStatus | null) {
    this.importConfig.linkStatus = type;
    this.emitConfigChange();
  }

  formatEnumValue(value: string): string {
    return value.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase());
  }

  // Update existing methods to use getCurrentPaymentConfigs
  selectAllInclude() {
    this.importConfig.includePaymentConfigUuids = [...this.getCurrentPaymentConfigs().map(c => c.uuid!)];
    this.importConfig.excludePaymentConfigUuids = [];
    this.emitConfigChange();
  }

  selectAllExclude() {
    this.importConfig.excludePaymentConfigUuids = [...this.getCurrentPaymentConfigs().map(c => c.uuid!)];
    this.importConfig.includePaymentConfigUuids = [];
    this.emitConfigChange();
  }

  get areAllIncluded(): boolean {
    const currentConfigs = this.getCurrentPaymentConfigs();
    return currentConfigs.length > 0 &&
      currentConfigs.every(config => this.isPaymentConfigIncluded(config.uuid!));
  }

  get areAllExcluded(): boolean {
    const currentConfigs = this.getCurrentPaymentConfigs();
    return currentConfigs.length > 0 &&
      currentConfigs.every(config => this.isPaymentConfigExcluded(config.uuid!));
  }

  parseMainSourceConfig(providerConfig: string): TransactionSourceConfig {
    return JSON.parse(providerConfig) as TransactionSourceConfig;
  }

  parseSupplierSourceConfig(providerConfig: string): SupplierSourceConfig {
    return JSON.parse(providerConfig) as SupplierSourceConfig;
  }

  // Helper method to filter configs by selected transaction sources
  private filterConfigsByTransactionSource(configs: PaymentConfigRequest[], type: 'single' | 'shared' | 'offering'): PaymentConfigRequest[] {
    // If transaction sources mode is 'all', return all configs
    if (this.isTransactionSourcesAllMode()) {
      return configs;
    }

    // If no specific sources are selected, return empty array
    if (this.importConfig.transactionSources.length === 0) {
      return [];
    }

    // Filter configs based on selected transaction sources
    if (type === 'single') {
      return configs.filter(config => {
        if (!config.transactionSource) return false;

        return this.importConfig.transactionSources.some(selectedSource =>
          config.transactionSource!.includes(selectedSource)
        );
      });
    }

    if (type === 'shared' && this.importConfig.transactionSources.includes(TransactionSource.VEND_SUPPLIER_LEASE)) {
      return configs;
    }

    if (type === 'offering' && this.importConfig.transactionSources.includes(TransactionSource.VEND_SUPPLIER_OFFERING)) {
      return configs;
    }

    return [];
  }

  // Update helper methods to always return filtered results
  getFilteredMasterConfigsByType(type: 'single' | 'shared' | 'offering'): PaymentConfigRequest[] {
    const configs = this.masterConfigs[type] || [];
    return this.filterConfigsByTransactionSource(configs, type);
  }

  getFilteredSharedConfigsByType(type: 'shared' | 'offering'): {
    parentName: string,
    configs: PaymentConfigRequest[]
  }[] {
    const groupedConfigs = this.sharedConfigs[type] || [];

    return groupedConfigs.map(group => ({
      parentName: group.parentName,
      configs: this.filterConfigsByTransactionSource(group.configs, type)
    })).filter(group => group.configs.length > 0); // Remove groups with no configs after filtering
  }

  // Update existing methods to use filtered versions
  getMasterConfigsByType(type: 'single' | 'shared' | 'offering'): PaymentConfigRequest[] {
    return this.getFilteredMasterConfigsByType(type);
  }

  getSharedConfigsByType(type: 'shared' | 'offering'): { parentName: string, configs: PaymentConfigRequest[] }[] {
    return this.getFilteredSharedConfigsByType(type);
  }

  getCurrentPaymentConfigs(): PaymentConfigRequest[] {
    if (this.importConfig.importAgreementType === ImportAgreementType.MASTER) {
      // Return all filtered master configs (single, shared, offering)
      return [
        ...this.getMasterConfigsByType('single'),
        ...this.getMasterConfigsByType('shared'),
        ...this.getMasterConfigsByType('offering')
      ];
    } else if (this.importConfig.importAgreementType === ImportAgreementType.SUB_LEASE) {
      // Flatten all filtered shared configs
      return [
        ...this.getSharedConfigsByType('shared').flatMap(group => group.configs),
        ...this.getSharedConfigsByType('offering').flatMap(group => group.configs)
      ];
    }
    return [];
  }

  private updateDataSources() {
    // Update master config datasources
    this.masterSingleDataSource.data = this.getFilteredMasterConfigsByType('single');
    this.masterSharedDataSource.data = this.getFilteredMasterConfigsByType('shared');
    this.masterOfferingDataSource.data = this.getFilteredMasterConfigsByType('offering');

    // Update shared config datasources (flattened)
    this.sharedConfigsDataSource.data = this.getFilteredSharedConfigsByType('shared').flatMap(group => group.configs);
    this.offeringConfigsDataSource.data = this.getFilteredSharedConfigsByType('offering').flatMap(group => group.configs);

    console.log('MASTER SINGLE', this.masterSingleDataSource);
    console.log('MASTER SHARED', this.masterSharedDataSource);
    console.log('MASTER OFFERING', this.masterOfferingDataSource);
    console.log('SHARED', this.sharedConfigsDataSource);
    console.log('OFFERING', this.offeringConfigsDataSource);
  }

  // Individual datasource getters
  getSingleConfigsDataSource(): MatTableDataSource<PaymentConfigRequest> {
    return this.masterSingleDataSource;
  }

  getSharedConfigsDataSource(): MatTableDataSource<PaymentConfigRequest> {
    return this.masterSharedDataSource;
  }

  getOfferingConfigsDataSource(): MatTableDataSource<PaymentConfigRequest> {
    return this.masterOfferingDataSource;
  }

  // Helper method to create datasource for a specific parent group
  createParentGroupDataSource(parentName: string, section: 'shared' | 'offering'): MatTableDataSource<PaymentConfigRequest> {
    const parentGroup = this.getSharedConfigsByType(section).find(group => group.parentName === parentName);
    return new MatTableDataSource(parentGroup ? parentGroup.configs : []);
  }

  get configCount() {
    if ((!this.importConfig.includePaymentConfigUuids || this.importConfig.includePaymentConfigUuids.length === 0)
    && (!this.importConfig.excludePaymentConfigUuids || this.importConfig.excludePaymentConfigUuids.length === 0)) {
      return this.getCurrentPaymentConfigs().length;
    }

    if (this.importConfig.includePaymentConfigUuids && this.importConfig.includePaymentConfigUuids.length > 0) {
      return this.importConfig.includePaymentConfigUuids.length;
    }

    if (this.importConfig.excludePaymentConfigUuids && this.importConfig.excludePaymentConfigUuids.length > 0) {
      return this.getCurrentPaymentConfigs().length - this.importConfig.excludePaymentConfigUuids.length;
    }

    return 0;
  }
}
